import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  canPersistConsent,
  hasAnalyticsConsent,
  hasConsentChoice,
  setConsent,
  type ConsentChoice,
} from "@/lib/privacyConsent";
import { initRollbar } from "@/lib/rollbar";

/**
 * Subtle, non-blocking privacy notice. Sits in the bottom corner with
 * glass styling — visible but not a full-width interrupt bar.
 */
export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!canPersistConsent()) {
      setVisible(false);
      return;
    }
    if (!hasConsentChoice()) {
      setVisible(true);
      return;
    }
    if (hasAnalyticsConsent()) {
      initRollbar();
    }
  }, []);

  const choose = (choice: ConsentChoice) => {
    setConsent(choice);
    if (choice === "analytics") {
      initRollbar();
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          role="dialog"
          aria-label="Cookie and privacy preferences"
          aria-live="polite"
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[100] safe-bottom pointer-events-none
            bottom-3 left-3 right-3
            sm:bottom-5 sm:right-5 sm:left-auto sm:max-w-[22rem]"
        >
          <div
            className="pointer-events-auto ios-card rounded-2xl p-card shadow-lg
              border border-border/60 bg-card/90 backdrop-blur-xl"
          >
            <div className="flex gap-3">
              <div
                className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center
                  rounded-xl bg-primary/10 text-primary"
                aria-hidden
              >
                <Shield className="h-4 w-4" />
              </div>

              <div className="min-w-0 flex-1 space-y-2">
                <p className="text-sm font-medium leading-snug text-foreground">
                  Your privacy matters
                </p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  We use essential storage for sign-in and preferences. Optional
                  diagnostics and offline cache help us fix issues faster — never
                  for ads.{" "}
                  {/*
                    Persistent underline, not `hover:underline`: axe's
                    `link-in-text-block` rule fails a link inside a paragraph
                    that is set apart by colour alone unless it clears 3:1
                    against the surrounding text. `text-primary/90` on
                    `text-muted-foreground` measures 1.08:1 in dark mode, so the
                    underline is what makes the link perceivable.
                  */}
                  <a
                    href="/privacy"
                    className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
                  >
                    Privacy policy
                  </a>
                </p>

                <div className="flex flex-wrap items-center gap-2 pt-0.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 px-3 text-xs text-muted-foreground tap-highlight"
                    onClick={() => choose("essential")}
                  >
                    Essential only
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 px-3 text-xs tap-highlight"
                    onClick={() => choose("analytics")}
                  >
                    Accept optional storage
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
